import { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { ALL_WORKSPACES_ID, IInsightsWorkspaceOption } from "v2/services/insights.service";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT } from "./tokens";

interface WorkspaceSelectorProps {
  workspaces: IInsightsWorkspaceOption[];
  value: string;
  onChange: (workspaceId: string) => void;
  disabled?: boolean;
}

interface WorkspaceGroup {
  id: string;
  label: string;
  cnpj: string;
  memberIds: string[];
}

// Vários workspaces do mesmo buyer consultam os mesmos dados no Snowflake —
// o backend resolve o CNPJ do buyer independente de qual workspace do grupo
// é passado, então o seletor mostra 1 opção por buyer, não por workspace.
// Agrupa por buyerOrganizationId (não por nome) porque filiais compartilham
// o mesmo corporateName mas são orgs distintas com CNPJs diferentes.
function groupWorkspacesByBuyer(workspaces: IInsightsWorkspaceOption[]): WorkspaceGroup[] {
  const groups: WorkspaceGroup[] = [];
  const groupByBuyerKey = new Map<string, WorkspaceGroup>();

  workspaces.forEach((workspace) => {
    const buyerKey = workspace.buyerOrganizationId || workspace.id;
    const existing = groupByBuyerKey.get(buyerKey);
    if (existing) {
      existing.memberIds.push(workspace.id);
      return;
    }
    // Filiais com o mesmo nome ficam distinguíveis pelo CNPJ no rótulo.
    const label = workspace.buyerName || workspace.label;
    const group: WorkspaceGroup = {
      id: workspace.id,
      label,
      cnpj: workspace.buyerFederalTaxId,
      memberIds: [workspace.id],
    };
    groupByBuyerKey.set(buyerKey, group);
    groups.push(group);
  });

  // Só desambigua com CNPJ quando há nomes repetidos (matriz + filiais).
  const nameCount = new Map<string, number>();
  groups.forEach((g) => nameCount.set(g.label, (nameCount.get(g.label) ?? 0) + 1));
  groups.forEach((g) => {
    if ((nameCount.get(g.label) ?? 0) > 1 && g.cnpj) {
      g.label = `${g.label} — ${g.cnpj}`;
    }
  });

  return groups;
}

export function WorkspaceSelector({
  workspaces,
  value,
  onChange,
  disabled,
}: WorkspaceSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Só 1 workspace no total: sem escolha a fazer — mesmo formato do botão de
  // anexar (ícone + tooltip), não o nome do workspace.
  if (workspaces.length <= 1) {
    return (
      <Tooltip title="Você só tem um workspace">
        <span>
          <IconButton size="small" disabled={disabled}>
            <BusinessOutlinedIcon fontSize="small" sx={{ color: `${CONDUCCO_PRIMARY} !important` }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const groups = groupWorkspacesByBuyer(workspaces);

  // Todos os workspaces pertencem ao mesmo buyer: mostra o nome do buyer como
  // opção única, sem menu de troca nem "Todos os workspaces" (não há o que
  // agrupar/alternar).
  if (groups.length === 1) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.75 }}>
        <Typography
          noWrap
          variant="body2"
          sx={{
            fontFamily: INSIGHTS_FONT,
            fontSize: "0.8125rem",
            color: `${CONDUCCO_PRIMARY} !important`,
            maxWidth: 200,
          }}
        >
          {groups[0].label}
        </Typography>
      </Box>
    );
  }

  const selectedGroup =
    value === ALL_WORKSPACES_ID
      ? undefined
      : groups.find((group) => group.memberIds.includes(value));
  const label =
    value === ALL_WORKSPACES_ID
      ? "Todos os workspaces"
      : selectedGroup?.label ?? "Selecione seu workspace";

  const handleSelect = (workspaceId: string) => {
    onChange(workspaceId);
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.25,
          py: 0.75,
          borderRadius: 5,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          maxWidth: 200,
          transition: "background-color 0.15s ease",
          "&:hover": disabled
            ? undefined
            : {
                backgroundColor: "rgba(48,36,225,0.06)",
              },
        }}
      >
        <Typography
          noWrap
          variant="body2"
          sx={{ fontFamily: INSIGHTS_FONT, fontSize: "0.8125rem", color: `${CONDUCCO_PRIMARY} !important` }}
        >
          {label}
        </Typography>
        <KeyboardArrowDownIcon fontSize="small" sx={{ color: `${CONDUCCO_PRIMARY} !important` }} />
      </Box>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem selected={value === ALL_WORKSPACES_ID} onClick={() => handleSelect(ALL_WORKSPACES_ID)}>
          <em>Todos os workspaces</em>
        </MenuItem>
        {groups.map((group) => (
          <MenuItem
            key={group.id}
            selected={group.memberIds.includes(value)}
            onClick={() => handleSelect(group.id)}
          >
            {group.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
